import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSearchHistory } from "../../store/leadsSlice";
import { formatSearchHistoryDate } from "../../utils/formatDate";
import {
    canCreateCampaignFromSearch,
    getSearchStatusBadge,
} from "../../utils/searchStatusUtils";

const getCreatedAtTime = (createdAt) => {
    if (!createdAt) return 0;

    const time = new Date(createdAt).getTime();

    return Number.isNaN(time) ? 0 : time;
};

const ProductSearchHistoryPanel = ({ product, onStartCampaignForSearch }) => {
    const dispatch = useDispatch();
    const { searchHistory, searchHistoryStatus, searchHistoryError } =
        useSelector((state) => state.leads);
    const [activeSearchId, setActiveSearchId] = useState(null);
    const [panelError, setPanelError] = useState("");

    const searches = useMemo(
        () =>
            (Array.isArray(searchHistory) ? searchHistory : [])
                .filter(
                    (search) =>
                        search?.is_deleted !== true &&
                        search?.id &&
                        search?.query_string?.trim(),
                )
                .sort(
                    (firstSearch, secondSearch) =>
                        getCreatedAtTime(secondSearch.created_at) -
                        getCreatedAtTime(firstSearch.created_at),
                ),
        [searchHistory],
    );
    useEffect(() => {
        dispatch(fetchSearchHistory());
    }, [dispatch]);

    useEffect(() => {
        setPanelError("");
        setActiveSearchId(null);
    }, [product]);

    const handleRetry = () => {
        dispatch(fetchSearchHistory());
    };

    const handleCreateCampaign = async (search) => {
        if (activeSearchId) return;

        setPanelError("");
        setActiveSearchId(search.id);

        try {
            const result = await onStartCampaignForSearch?.(search);

            if (!result?.ok) {
                if (result?.showPanelError !== false) {
                    setPanelError(
                        result?.message ||
                            "Unable to prepare a campaign for this search.",
                    );
                }
            }
        } finally {
            setActiveSearchId(null);
        }
    };

    if (
        searchHistoryStatus === "idle" ||
        searchHistoryStatus === "loading"
    ) {
        return (
            <div className="m-4 rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-800">
                <div className="flex items-center gap-2 font-semibold">
                    <Loader2 size={16} className="animate-spin" />
                    Loading searches...
                </div>
            </div>
        );
    }

    if (searchHistoryStatus === "failed") {
        return (
            <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <p className="font-semibold">Unable to load search history.</p>
                {searchHistoryError && (
                    <p className="mt-1">{searchHistoryError}</p>
                )}
                <button
                    type="button"
                    onClick={handleRetry}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                >
                    <RefreshCw size={15} />
                    Retry
                </button>
            </div>
        );
    }

    if (searches.length === 0) {
        return (
            <div className="m-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">
                    No previous searches found.
                </p>
                <p className="mt-1">
                    Search for leads first, then create a campaign using this
                    product.
                </p>
            </div>
        );
    }

    return (
        <div>
            {panelError && (
                <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {panelError}
                </div>
            )}

            <div>
                {searches.map((search) => {
                    const statusBadge = getSearchStatusBadge(search.status);
                    const canCreateCampaign = canCreateCampaignFromSearch(
                        search.status,
                    );
                    const isPreparing = activeSearchId === search.id;

                    return (
                        <article
                            key={search.id}
                            className={`border-b border-slate-200 px-4 py-3 transition-colors ${
                                isPreparing
                                    ? "border-l-4 border-l-[#f97316] bg-[#fff7ed] pl-3 shadow-[inset_0_0_0_1px_rgba(249,115,22,0.18)]"
                                    : "border-l-4 border-l-transparent odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                            }`}
                        >
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between xl:flex-col xl:items-stretch xl:justify-start">
                                <div className="min-w-0">
                                    <div className="flex items-start justify-between gap-3">
                                        <h4
                                            className={`truncate text-base font-semibold ${
                                                isPreparing
                                                    ? "text-[#7c2d12]"
                                                    : "text-slate-900"
                                            }`}
                                            title={search.query_string}
                                        >
                                            {search.query_string}
                                        </h4>
                                        {statusBadge.label ? (
                                            <span
                                                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusBadge.className}`}
                                            >
                                                {statusBadge.label}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="mt-1 text-xs text-slate-500">
                                        {formatSearchHistoryDate(
                                            search.created_at,
                                        )}
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        handleCreateCampaign(search)
                                    }
                                    disabled={
                                        !canCreateCampaign ||
                                        Boolean(activeSearchId)
                                    }
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 md:w-fit md:shrink-0 md:whitespace-nowrap xl:w-full"
                                >
                                    {isPreparing && (
                                        <Loader2
                                            size={15}
                                            className="animate-spin"
                                            aria-hidden="true"
                                        />
                                    )}
                                    {isPreparing
                                        ? "Preparing..."
                                        : "Select Search to Create Campaign"}
                                </button>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default ProductSearchHistoryPanel;
